import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { PermissionGate } from "@/components/common/PermissionGate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  RotateCw,
  RefreshCw,
  Wrench,
  Undo2,
  Ban,
} from "lucide-react";
import {
  useAdminDomainOperations,
  useRetryDomain,
  useReconcileDomain,
  useRefundDomain,
  useCancelDomain,
  useRefreshTransfer,
} from "@/hooks/useAdminDomainQueries";
import {
  DomainReconcileDialog,
  DomainActionConfirmDialog,
  RegistrarDriftPanel,
} from "@/components/admin/domain-operations";
import type { DomainOperationDto } from "@/types/domain";
import { formatDateTime } from "@/lib/utils";

/**
 * Maps an attention reason to a Badge variant.
 *
 * - Hard failures ("RegistrationFailed", "TransferFailed") use destructive.
 * - Stuck states ("StuckRegistering", "StuckTransferPending") use secondary
 *   to signal "in-flight but suspicious" without screaming.
 * - Any future backend-added reason falls through to outline.
 */
function attentionVariant(
  attentionReason: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (attentionReason) {
    case "RegistrationFailed":
    case "TransferFailed":
      return "destructive";
    case "StuckRegistering":
    case "StuckTransferPending":
      return "secondary";
    default:
      return "outline";
  }
}

/** Formats an ISO date string for display, or "—" when null/undefined. */
function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return formatDateTime(value);
}

/**
 * Formats the optional DNS record count for the dashboard column.
 * Returns "—" when the backend did not include the field, "0" when
 * the domain genuinely has no DNS records.
 */
function formatDnsCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
}

export function DomainOperationsDashboardPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  /**
   * Days threshold for stale TransferPending domains (P2).
   * `undefined` means "use the backend default" (typically 7 days).
   * Bound to the new filter input below.
   */
  const [stalePendingDays, setStalePendingDays] = useState<number | undefined>(
    undefined,
  );

  // Dialog state — at most one action dialog open at a time.
  const [reconcileTarget, setReconcileTarget] =
    useState<DomainOperationDto | null>(null);
  const [refundTarget, setRefundTarget] = useState<DomainOperationDto | null>(
    null,
  );
  const [cancelTarget, setCancelTarget] = useState<DomainOperationDto | null>(
    null,
  );

  const filters = { page, pageSize, stalePendingDays };
  const { data, isLoading, error, refetch } = useAdminDomainOperations(filters);

  const retryDomain = useRetryDomain();
  const reconcileDomain = useReconcileDomain();
  const refundDomain = useRefundDomain();
  const cancelDomain = useCancelDomain();
  const refreshTransfer = useRefreshTransfer();

  const domains = data?.data ?? [];
  const pagination = data?.pagination;
  const totalCount = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.currentPage ?? 1;

  const startItem = domains.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(
    currentPage * (pagination?.itemsPerPage ?? pageSize),
    totalCount,
  );

  /** True while any mutation targeting `id` is in flight. */
  const isRowBusy = (id: string) =>
    (retryDomain.isPending && retryDomain.variables === id) ||
    (reconcileDomain.isPending && reconcileDomain.variables?.id === id) ||
    (refundDomain.isPending && refundDomain.variables?.id === id) ||
    (cancelDomain.isPending && cancelDomain.variables?.id === id) ||
    (refreshTransfer.isPending && refreshTransfer.variables === id);

  const handleReconcileSubmit = (formData: {
    registrarReference: string;
    expiresAt: string;
  }) => {
    if (!reconcileTarget) return;
    reconcileDomain.mutate(
      {
        id: reconcileTarget.id,
        data: {
          registrarReference: formData.registrarReference,
          // Convert the YYYY-MM-DD date value to an explicit UTC instant so the
          // backend DateTime parser receives an unambiguous ISO-8601 timestamp.
          expiresAt: `${formData.expiresAt}T00:00:00Z`,
        },
      },
      { onSuccess: () => setReconcileTarget(null) },
    );
  };

  const handleRefundConfirm = (reason: string) => {
    if (!refundTarget) return;
    refundDomain.mutate(
      { id: refundTarget.id, data: { reason } },
      { onSuccess: () => setRefundTarget(null) },
    );
  };

  const handleCancelConfirm = (reason: string) => {
    if (!cancelTarget) return;
    cancelDomain.mutate(
      { id: cancelTarget.id, data: { reason } },
      { onSuccess: () => setCancelTarget(null) },
    );
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Domain Operations Dashboard"
          subtitle="Triage and recover domain registrations that need attention, including stalled transfer-in operations"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">
                Failed to load the operations dashboard
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domain Operations Dashboard"
        subtitle="Triage and recover domain registrations that need attention, including stalled transfer-in operations"
      />

      {/* Summary Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Domains Needing Attention
          </CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalCount}</div>
          <p className="text-xs text-muted-foreground">
            Failed registrations, stuck Registering, failed transfers, and stale
            TransferPending registrations
          </p>
        </CardContent>
      </Card>

      {/* Data Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Operations Queue ({totalCount})</CardTitle>
              <CardDescription>
                {totalCount > 0
                  ? `Showing ${startItem}–${endItem} of ${totalCount}`
                  : "No domains need attention"}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Label
                  htmlFor="stale-pending-days"
                  className="whitespace-nowrap text-sm text-muted-foreground"
                >
                  Stale transfer (days)
                </Label>
                <Input
                  id="stale-pending-days"
                  type="number"
                  min={1}
                  max={365}
                  placeholder="Default"
                  className="w-[110px]"
                  value={stalePendingDays ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    if (raw === "") {
                      setStalePendingDays(undefined);
                    } else {
                      const parsed = parseInt(raw, 10);
                      setStalePendingDays(
                        Number.isFinite(parsed) && parsed > 0
                          ? parsed
                          : undefined,
                      );
                    }
                    setPage(1);
                  }}
                />
              </div>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(parseInt(value, 10));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Loading the operations queue...
              </p>
            </div>
          ) : domains.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No domains need attention</p>
              <p className="mt-2 text-sm text-muted-foreground">
                All domain registrations are healthy
              </p>
            </div>
          ) : (
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Domain</TableHead>
                    <TableHead scope="col">Status</TableHead>
                    <TableHead scope="col">Reason</TableHead>
                    <TableHead scope="col">Tenant</TableHead>
                    <TableHead scope="col">Registrar</TableHead>
                    <TableHead scope="col" className="text-right">
                      DNS records
                    </TableHead>
                    <TableHead scope="col">Failure</TableHead>
                    <TableHead scope="col">Updated</TableHead>
                    <TableHead scope="col" className="text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domains.map((domain) => {
                    const busy = isRowBusy(domain.id);
                    return (
                      <TableRow key={domain.id}>
                        <TableCell className="font-medium">
                          {domain.fqdn}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{domain.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={attentionVariant(domain.attentionReason)}
                          >
                            {domain.attentionReason}
                          </Badge>
                        </TableCell>
                        <TableCell>{domain.tenantName || "—"}</TableCell>
                        <TableCell>{domain.registrarProvider}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDnsCount(domain.dnsRecordCount)}
                        </TableCell>
                        <TableCell className="max-w-[240px]">
                          {domain.failureReason ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block truncate text-sm text-destructive-ink">
                                  {domain.failureReason}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                {domain.failureReason}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(domain.updatedAt)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() => retryDomain.mutate(domain.id)}
                            >
                              <RotateCw className="mr-1 h-4 w-4" />
                              Retry
                            </Button>
                            {domain.status === "TransferPending" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() =>
                                  refreshTransfer.mutate(domain.id)
                                }
                                title="Re-check transfer status at the registrar"
                              >
                                <RefreshCw className="mr-1 h-4 w-4" />
                                Refresh transfer
                              </Button>
                            )}
                            {/* Retry above is SuperAdmin+SupportAdmin; reconcile, refund and
                                cancel are SuperAdmin-only. */}
                            <PermissionGate roles="SuperAdmin">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => setReconcileTarget(domain)}
                              >
                                <Wrench className="mr-1 h-4 w-4" />
                                Reconcile
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => setRefundTarget(domain)}
                              >
                                <Undo2 className="mr-1 h-4 w-4" />
                                Refund
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy}
                                onClick={() => setCancelTarget(domain)}
                              >
                                <Ban className="mr-1 h-4 w-4" />
                                Cancel
                              </Button>
                            </PermissionGate>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Reconcile Dialog */}
      <DomainReconcileDialog
        domain={reconcileTarget}
        onClose={() => setReconcileTarget(null)}
        onSubmit={handleReconcileSubmit}
        isSubmitting={reconcileDomain.isPending}
      />

      {/* Refund Confirm Dialog */}
      <DomainActionConfirmDialog
        open={refundTarget !== null}
        title={`Refund ${refundTarget?.fqdn ?? ""}`}
        description="Issues a credit-note invoice for this domain order line and cancels the registration. This cannot be undone."
        confirmLabel="Issue Refund"
        isSubmitting={refundDomain.isPending}
        onClose={() => setRefundTarget(null)}
        onConfirm={handleRefundConfirm}
      />

      {/* Cancel Confirm Dialog */}
      <DomainActionConfirmDialog
        open={cancelTarget !== null}
        title={`Cancel ${cancelTarget?.fqdn ?? ""}`}
        description="Marks the registration Cancelled without a refund. Use this for customer-requested abandonment."
        confirmLabel="Cancel Registration"
        isSubmitting={cancelDomain.isPending}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
      />

      {/* Registrar Drift Panel */}
      <RegistrarDriftPanel />
    </div>
  );
}
